import pclnExperimentation from '@pcln/experimentation'
import type { PackageFormTripType, PackagesFormState } from '@pcln/horizon-search-form'
import { FLY_DRIVE, isHotelFlight, isHotelFlightCar, PackageSearchForm } from '@pcln/horizon-search-form'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useFlightAirportNesting } from '@/components/TypeAhead/useFlightAirportNesting'
import useSeti from '@/hooks/useSeti'
import logSearchFormAnalytics from '@/shared-utils/log-search-form-analytics'

import {
  fireAdvancedPackageSearchLinkClick,
  fireCustomizeCarCheckboxClick,
  fireCustomizeHotelCheckboxClick,
  trackCustomPackageSearchPromotionClick,
  trackCustomPackageSearchPromotionView,
} from '../ga4'
import { maybeFireFlyLapInfantTicketingImpression } from '../pkgEnableFlyLapInfantTicketing'
import { useVacationVibeEntry } from '../SearchForm/useVacationVibeEntry'
import { VacationVibeEntryBanner } from '../SearchForm/VacationVibeEntryBanner'
import type { PackagesFormStateType } from '../types'
import usePackagesFormInitialValues from './usePackagesFormInitialValues'

const DEFAULT_PICKUP_TIME = '12:00'
const DEFAULT_DROPOFF_TIME = '12:00'
const ADVANCED_SEARCH_DEFAULT_COPY = 'Custom Package Search (One-Way Flight, Multi-Hotel)'
const ADVANCED_SEARCH_VARIANT_COPY = 'Search multiple dates or destinations'

export default function HorizonPackagesForm({
  onSubmit,
  pageName,
}: Readonly<{
  onSubmit: (pageName: 'homepage' | 'landing', data: PackagesFormStateType) => void
  pageName: 'homepage' | 'landing'
}>) {
  const { initialValues, isMobile, cguid, localeCode } = usePackagesFormInitialValues({ pageName })
  const showVacationVibeEntry = useVacationVibeEntry()
  const flyTypeaheadSeti = useSeti({ experimentCode: 'PKG_AC_FLY_TYPEAHEAD', shouldFireImpression: false })
  const useFlightTypeaheadForFlyDrive = flyTypeaheadSeti === 'VARIANT'

  const isSearchMultipleDatesVariant =
    useSeti({
      experimentCode: 'PKG_ADVANCED_SEARCH_COPY_UPDATE_PT2',
      shouldFireImpression: false,
    }) === 'VARIANT'
  const hasFiredCopyUpdateImpressionRef = useRef(false)
  const fireCopyUpdateImpression = () => {
    if (!hasFiredCopyUpdateImpressionRef.current) {
      hasFiredCopyUpdateImpressionRef.current = true
      pclnExperimentation.fireImpression('PKG_ADVANCED_SEARCH_COPY_UPDATE_PT2')
      trackCustomPackageSearchPromotionView(pageName)
    }
  }
  const isCustomizeHotelCopyVariant =
    useSeti({ experimentCode: 'PKG_SEARCH_CUSTOMIZE_HOTEL_COPY', shouldFireImpression: false }) === 'VARIANT'
  const shouldAllowInternationalLapInfants =
    useSeti({
      experimentCode: 'PKG_ENABLE_FLY_LAP_INFANT_TICKETING',
      shouldFireImpression: false,
    }) === 'VARIANT'

  const [currentTripType, setCurrentTripType] = useState<PackageFormTripType>(initialValues.tripType)

  // WEB_MWEB_FLIGHT_SEARCH_AIRPORT_NESTING — only Fly+Drive's destination field uses the flights
  // endpoint, and only under PKG_AC_FLY_TYPEAHEAD. Passing a non-flights endpoint otherwise keeps
  // the hook from recording exposure for trip types that never render a flights typeahead.
  const rendersFlightsTypeahead = useFlightTypeaheadForFlyDrive && currentTripType === FLY_DRIVE
  const airportNestingEnabled = useFlightAirportNesting(
    rendersFlightsTypeahead ? 'flights' : 'packagesDestination',
  )
  const hasFiredCustomizeHotelImpression = useRef(false)
  useEffect(() => {
    if (
      (isHotelFlight(currentTripType) || isHotelFlightCar(currentTripType)) &&
      !hasFiredCustomizeHotelImpression.current
    ) {
      pclnExperimentation.fireImpression('PKG_SEARCH_CUSTOMIZE_HOTEL_COPY')
      hasFiredCustomizeHotelImpression.current = true
    }
  }, [currentTripType])

  const handleSubmit = useCallback(
    (data: PackagesFormState) => {
      if (data.tripType === 'FLY:DRIVE') {
        pclnExperimentation.fireImpression('PKG_AC_FLY_TYPEAHEAD')
      }
      onSubmit(pageName, data as PackagesFormStateType)
      logSearchFormAnalytics({ cguid, pageName, productType: 'vacations', isMobile })
    },
    [onSubmit, pageName, cguid, isMobile],
  )

  return (
    <div className='flex flex-col gap-4'>
      <PackageSearchForm
        initialValues={initialValues as unknown as Partial<PackagesFormState>}
        isMobile={isMobile}
        autoAdvance={!isMobile}
        localeCode={localeCode}
        pageName={pageName}
        defaultPickupTime={DEFAULT_PICKUP_TIME}
        defaultDropoffTime={DEFAULT_DROPOFF_TIME}
        useFlightTypeaheadForFlyDrive={useFlightTypeaheadForFlyDrive}
        advancedSearchCopy={
          isSearchMultipleDatesVariant ? ADVANCED_SEARCH_VARIANT_COPY : ADVANCED_SEARCH_DEFAULT_COPY
        }
        onAdvancedSearchClick={() => {
          trackCustomPackageSearchPromotionClick(pageName)
          fireAdvancedPackageSearchLinkClick(pageName)
        }}
        onAdvancedSearchVisible={fireCopyUpdateImpression}
        hotelCarCheckboxLabel='Customize Car Search'
        onHotelCarCheckboxToggle={() => fireCustomizeCarCheckboxClick(pageName)}
        partialStayCheckboxLabel={isCustomizeHotelCopyVariant ? 'Customize Hotel Search' : undefined}
        onPartialStayCheckboxToggle={() =>
          fireCustomizeHotelCheckboxClick(pageName, isHotelFlightCar(currentTripType) ? 'ahc' : 'ha')
        }
        onTripTypeChange={setCurrentTripType}
        onSearchCriteriaChange={maybeFireFlyLapInfantTicketingImpression}
        shouldAllowInternationalLapInfants={shouldAllowInternationalLapInfants}
        onSubmit={handleSubmit}
        autocompleteOptions={({ typeAheadEndpoint }) => ({
          isMobile,
          airportNestingEnabled: typeAheadEndpoint === 'flights' && airportNestingEnabled,
        })}
      />
      {showVacationVibeEntry && <VacationVibeEntryBanner pageName={pageName} />}
    </div>
  )
}
